/**
 * LA PLACA DEL VEHÍCULO — UNA SOLA FUENTE DE VERDAD.
 *
 * ═══ POR QUÉ EXISTE ESTE ARCHIVO ═══
 *
 * El 10 de septiembre de 2026 el fundador escribió `A43A548` en su propio
 * perfil de chofer para probar la seguridad, y la app lo aceptó y lo guardó.
 * Quedó medido en Firestore con hora: `users/{uid}.licensePlate = 'A43A548'`,
 * escrito a las 19:11 de ese día.
 *
 * La validación de entonces, entera, era ésta:
 *
 *     if (!cleanPlate || cleanPlate.length < 4) { ...avisa... }
 *
 * Cuatro caracteres cualesquiera. `HOLA`, `1234`, `....` pasaban.
 *
 * ESO NO ES UN DETALLE DE FORMULARIO. En una app de viajes la placa es el
 * ÚNICO dato que el pasajero puede comprobar con sus propios ojos antes de
 * montarse en un carro con un desconocido. Es el cierre de todo el KYC: la
 * cédula la verificamos nosotros, la cara la verificamos nosotros, pero el
 * carro lo verifica el pasajero en la acera, comparando la placa de metal con
 * la que dice la pantalla. Si ese número lo puede escribir cualquiera y puede
 * ser cualquier cosa, el pasajero cree que está comprobando algo y no está
 * comprobando nada.
 *
 * ═══ DE DÓNDE SALE EL FORMATO (no es inventado) ═══
 *
 * La DGII es quien asigna las placas en República Dominicana. Su norma y su
 * folleto de tipos de placas describen la misma estructura: UNA O DOS LETRAS
 * INICIALES seguidas de CINCO O SEIS DÍGITOS correlativos. La letra dice el
 * tipo de vehículo (A automóvil, K motocicleta, I autobús privado, L carga,
 * entre otras) y hay prefijos de dos letras para oficiales, diplomáticas y
 * exoneradas (OE, OF, OM, OP, EA, EG, EL, EM, ED, EI, VC, WD, OI, EX, YX, NZ,
 * DD). Los dos ejemplos que la propia app ya le mostraba al chofer desde antes
 * —`A123456` y `K12345`— encajan exactamente con eso.
 *
 * ⚠️ NO SE FILTRA POR LETRA, A PROPÓSITO. Se acepta cualquier letra inicial.
 * Una lista blanca de prefijos parece más segura y es peor: el día que la DGII
 * emita una serie con una letra que no esté en la lista, un chofer legítimo se
 * queda sin poder trabajar y nadie sabrá por qué. La ESTRUCTURA (letras al
 * frente, números detrás) es certeza; el juego exacto de letras no lo es. Se
 * valida lo que se sabe.
 *
 * Por la misma razón se admiten de 4 a 7 dígitos en vez de exactamente 5 o 6:
 * un dígito de margen a cada lado por si existe una serie antigua o nueva que
 * no conozcamos. Lo que se cierra es la basura, no los casos raros.
 *
 * ═══ POR QUÉ SE PUEDE PONER ESTE FILTRO HOY SIN ROMPER NADA ═══
 *
 * Medido antes de escribir una línea, leyendo Firestore por la API REST:
 * en TODA la base de datos hay 22 usuarios, 5 con perfil de chofer, y UNA sola
 * placa escrita — la `A43A548` de la prueba del fundador, de ese mismo día.
 * Cero placas en `/rides`, cero en `/drivers`, cero en `/vehicles`. O sea: no
 * hay un solo dato real que este filtro pueda dejar fuera. Si esto se hubiera
 * puesto con mil choferes trabajando, habría que migrar primero; hoy no.
 *
 * ═══ LO QUE ESTO NO HACE, Y HAY QUE DECIRLO ═══
 *
 * Un filtro de formato impide la basura. NO prueba que la placa sea de ese
 * carro: `A439548` tiene la forma correcta y puede ser inventada. Lo único que
 * ata la placa al vehículo es la FOTO de la placa que el KYC ya exige ("2. Lado
 * de la Placa — la placa perfectamente legible"), comparada por una persona.
 * Por eso el otro arreglo de esta ronda es que cambiar la placa DESPUÉS de
 * estar verificado quede registrado y avisado, no que baste con el formato.
 */

/** Ejemplo de placa de carro — el que ya usaba la app en sus textos. */
export const EJEMPLO_PLACA_CARRO = 'A123456';

/** Ejemplo de placa de motor. */
export const EJEMPLO_PLACA_MOTO = 'K12345';

/**
 * La forma de una placa dominicana: 1 o 2 letras y luego SOLO dígitos.
 *
 * Se exporta porque `firestore.rules` lleva esta MISMA expresión y las dos
 * tienen que decir lo mismo. Si se cambia aquí, hay que cambiarla allá: el
 * cliente da el mensaje bonito, la regla es la que de verdad cierra la puerta
 * (un chofer puede escribir en Firestore por la API REST sin pasar por la app).
 */
export const FORMA_PLACA = /^[A-Z]{1,2}[0-9]{4,7}$/;

/**
 * Deja la placa como se va a guardar: mayúsculas y sin nada que no sea letra o
 * número. Así `a-123456`, `A 123 456` y `a123456` acaban siendo la MISMA placa.
 *
 * Esto importa más de lo que parece: si dos choferes escriben la misma placa
 * con distinto guion, son dos textos distintos y cualquier comprobación futura
 * de "esta placa ya está usada" fallaría sin que nadie lo note.
 */
export function normalizarPlaca(valor: unknown): string {
  return String(valor ?? '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
}

export type RevisionPlaca =
  | { ok: true; placa: string }
  | { ok: false; placa: string; titulo: string; motivo: string };

/**
 * Revisa una placa y, si está mal, dice EXACTAMENTE qué corregir.
 *
 * El mensaje no es adorno. El chofer que recibe "placa inválida" vuelve a
 * escribir lo mismo y se enfada; el que recibe "después de la letra van solo
 * números" lo arregla de una vez. Cada rama de abajo existe porque es un error
 * distinto que una persona real comete.
 */
export function revisarPlaca(valor: unknown, esMoto = false): RevisionPlaca {
  const placa = normalizarPlaca(valor);
  const ejemplo = esMoto ? EJEMPLO_PLACA_MOTO : EJEMPLO_PLACA_CARRO;

  if (!placa) {
    return {
      ok: false,
      placa,
      titulo: 'Falta la placa',
      motivo:
        `Escribe la placa de tu ${esMoto ? 'motor' : 'vehículo'} completa, como está en el metal. ` +
        `Por ejemplo: ${ejemplo}.`,
    };
  }

  const letras = placa.match(/^[A-Z]+/);
  const digitos = placa.replace(/^[A-Z]+/, '');

  if (!letras) {
    return {
      ok: false,
      placa,
      titulo: 'La placa empieza con una letra',
      motivo:
        `Las placas dominicanas empiezan con letra y siguen con números. ` +
        `Por ejemplo: ${ejemplo}.`,
    };
  }

  if (letras[0].length > 2) {
    return {
      ok: false,
      placa,
      titulo: 'Sobran letras al principio',
      motivo: `Una placa lleva una o dos letras delante y después solo números. Por ejemplo: ${ejemplo}.`,
    };
  }

  if (!digitos) {
    return {
      ok: false,
      placa,
      titulo: 'Faltan los números de la placa',
      motivo: `Después de la letra van los números. Por ejemplo: ${ejemplo}.`,
    };
  }

  // Este es el caso que el fundador probó: `A43A548`. Una letra metida en medio
  // de los números. Antes pasaba porque solo se contaban caracteres.
  if (/[A-Z]/.test(digitos)) {
    return {
      ok: false,
      placa,
      titulo: 'Revisa la placa',
      motivo:
        `Después de la letra del principio van SOLO números, sin letras en medio. ` +
        `Por ejemplo: ${ejemplo}.`,
    };
  }

  if (digitos.length < 4) {
    return {
      ok: false,
      placa,
      titulo: 'Faltan números en la placa',
      motivo: `Escribe la placa completa, con todos sus números. Por ejemplo: ${ejemplo}.`,
    };
  }

  if (digitos.length > 7) {
    return {
      ok: false,
      placa,
      titulo: 'Sobran números en la placa',
      motivo: `Revisa que no hayas escrito un número de más. Por ejemplo: ${ejemplo}.`,
    };
  }

  // Sin esto, `A000000` —el relleno de ejemplo más típico— pasaría. No se
  // rechazan otras repeticiones (`A111111` podría ser una placa real y
  // bloquearla sería peor que dejarla pasar), solo el todo-ceros, que la DGII
  // no emite.
  if (/^0+$/.test(digitos)) {
    return {
      ok: false,
      placa,
      titulo: 'Esa no es una placa',
      motivo: `Escribe la placa real de tu ${esMoto ? 'motor' : 'vehículo'}. Por ejemplo: ${ejemplo}.`,
    };
  }

  if (!FORMA_PLACA.test(placa)) {
    // Red de seguridad: si alguna rama de arriba se queda corta, no se cuela
    // nada. Llegar aquí significa que falta un mensaje específico arriba.
    return {
      ok: false,
      placa,
      titulo: 'Revisa la placa',
      motivo: `Escríbela como está en el metal, sin espacios ni guiones. Por ejemplo: ${ejemplo}.`,
    };
  }

  return { ok: true, placa };
}

/** Atajo para cuando solo hace falta saber sí o no (guardas, reglas, filtros). */
export function placaValida(valor: unknown): boolean {
  return revisarPlaca(valor).ok;
}

/**
 * Texto de ayuda debajo del campo. Va aquí para que las cuatro pantallas que
 * piden placa digan lo mismo — antes cada una tenía su propio texto y su propia
 * idea de qué era válido.
 */
export function ayudaPlaca(esMoto = false): string {
  return esMoto
    ? `Como está en el metal, sin espacios. Ejemplo: ${EJEMPLO_PLACA_MOTO}.`
    : `Como está en el metal, sin espacios. Ejemplo: ${EJEMPLO_PLACA_CARRO}.`;
}
